import React, { useRef, useState } from 'react'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageEyebrow,
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { WorkspaceOutlineIcon } from 'components/Layout/LeftNav/nav-icons'
import { PinToHomepageButton } from 'components/QuickAccess/PinToHomepageButton'
import { useRecordVisit } from 'components/QuickAccess/useRecordVisit'
import { PAGE_KEYS } from 'services/homepage'
import {
  ProductTable,
  type ProductTableHandle,
} from 'components/Tables/ProductTable'
import FbButton from 'components/FbUI/FbButton'
import { Plus } from 'lucide-react'
import { PiInfo } from 'react-icons/pi'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import { useProductsViewModeStore } from 'stores/productsViewModeStore'
import { useAuth } from 'context/authentication/useAuth'

const productDescriptionText = `Current LTV Drivers: Cuisine, review composition, cross-platform ratings, menu dynamics, expense quartiles, chain size, demographics, population density, repeater rate, awareness effect, user inputs (churn, sales, etc). Allow up to an hour for LTV to calculate when updated.`

type ProductsTab = 'products' | 'variants'

export const ProductsPage: React.FC = () => {
  const { isLimited } = useAuth()
  const { viewMode, setViewMode } = useProductsViewModeStore()
  const tableRef = useRef<ProductTableHandle>(null)
  const [productsCount, setProductsCount] = useState<number | null>(null)
  const [variantsCount, setVariantsCount] = useState<number | null>(null)
  useRecordVisit({ targetType: 'page', pageKey: PAGE_KEYS.products })

  const handleCreateClick = () => {
    if (viewMode === 'products') {
      tableRef.current?.openCreateProductModal()
    } else {
      tableRef.current?.openCreateVariantModal()
    }
  }

  return (
    <FBPage
      innerPageClassName="!bg-surface-page !p-[8px]"
      containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
      // LTV context as a flush bottom banner (the sequence-wizard pattern).
      // Rendered via footerChildren so it sits at the page level — outside the
      // rounded, overflow-hidden Container — and spans edge-to-edge along the
      // very bottom instead of being clipped by the card's corner radius.
      footerChildren={
        <div className="flex items-start gap-2 border-t border-alert-default-border bg-alert-default-bg px-4 py-3 text-sm text-alert-default-title">
          <PiInfo size={18} className="mt-0.5 shrink-0" />
          <p>First Bite LTV Version 1.4 — {productDescriptionText}</p>
        </div>
      }
    >
      <FBPageHeader className="!mb-6">
        <FBPageTitleRow>
          <div className="flex flex-col items-start">
            <FBPageEyebrow>
              <WorkspaceOutlineIcon size={14} />
              Workspace
            </FBPageEyebrow>
            <FBPageTitle className="!text-fg">
              {viewMode === 'products' ? 'Products' : 'Product Variants'}
            </FBPageTitle>
          </div>
          <div className="flex items-center gap-2">
            <FbButton
              disabled={isLimited}
              className="!bg-brand-bright !text-white hover:!bg-brand-active"
              onClick={handleCreateClick}
            >
              <Plus size={16} />
              {viewMode === 'products' ? 'Create Product' : 'Create Variant'}
            </FbButton>
            <PinToHomepageButton
              target={{ targetType: 'page', pageKey: PAGE_KEYS.products }}
            />
          </div>
        </FBPageTitleRow>
      </FBPageHeader>

      <div className="mb-3 flex items-end">
        <UnderlineTabs<ProductsTab>
          tabs={[
            { key: 'products', title: 'Products', count: productsCount },
            {
              key: 'variants',
              title: 'Product Variants',
              count: variantsCount,
            },
          ]}
          activeKey={viewMode}
          onChange={setViewMode}
          idPrefix="products-page"
          aria-label="Products and variants"
          size="sm"
        />
      </div>

      <TableLinkStyleProvider variant="plain">
        <FBPageTableContainer
          className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
          style={{
            height: 'calc(var(--base-page-table-height) - 150px)',
          }}
        >
          {/* sm toolbar density (32px search + Filter/Columns), matching the
              other table pages. */}
          <ToolbarDensityProvider value="sm">
            <ProductTable
              ref={tableRef}
              tableKey="products-page-table"
              onProductsCountChange={setProductsCount}
              onVariantsCountChange={setVariantsCount}
            />
          </ToolbarDensityProvider>
        </FBPageTableContainer>
      </TableLinkStyleProvider>
    </FBPage>
  )
}
